// As ações do KDS, descritas de um jeito que dá pra SEGURAR antes de gravar.
//
// Botão Desfazer (dono, 03/10/2026): toque por engano acontece, e hoje marcar
// pronto avisa o cliente pelo WhatsApp na hora, Concluir lança a venda no
// caixa e Chamar entregador abre corrida. Então nenhuma ação sai mais direto
// pro servidor: ela vira um objeto deste arquivo, a tela mostra o resultado
// na hora (`aplicar`) e só depois de 10 s — ou quando a pessoa faz outra ação
// — as `requisicoes` são enviadas (hooks/useDesfazer.js).
//
//   titulo / alvo   o que a barra do Desfazer mostra ("Marcou como pronto" · "Mesa 4 · #1234")
//   aplicar(lista)  como ESTA tela fica enquanto a ação espera (e enquanto grava)
//   requisicoes     o que vai pro servidor, na ordem; `repetivel` = mandar duas vezes não estraga
//                   (só importa se a página for fechada com a requisição no ar)
//   erro            aviso quando o servidor recusa
//   chave/inversaDe quem desfaz quem: tocar no "Desfazer" do item cancela a marcação
//                   que ainda estava em espera, em vez de gravar as duas
//   sucesso         aviso de quando grava na hora (loja com o Desfazer desligado)
import { API, ehDeMesa, rotuloMesa, rotuloOrigem } from "./kds";

const nomeDe = (item) => item?.nome || item?.name || "produto";
const codigoDe = (pedido) => pedido?.codigo || `#${String(pedido?.id || "").slice(0, 5)}`;

/** "Mesa 4 · #1234" / "Delivery · Entrega · #1234" — de qual comanda foi a ação. */
export function alvoDe(pedido) {
  return `${ehDeMesa(pedido) ? rotuloMesa(pedido) : rotuloOrigem(pedido)} · ${codigoDe(pedido)}`;
}

const noPedido = (lista, id, muda) => lista.map((p) => (p.id === id ? muda(p) : p));
const nosItens = (pedido, idxs, muda) => ({ ...pedido, items: (pedido.items || []).map((i) => (idxs.includes(i._idx) ? muda(i) : i)) });
const patchItem = (pedido, item, corpo) => ({
  metodo: "patch", url: `${API}/kds/pedidos/${pedido.id}/itens/${item._idx}`,
  corpo: { ...corpo, fingerprint: item._fp }, repetivel: true,
});
const chave = (tipo, pedido, itens) => `${tipo}:${pedido.id}:${itens.map((i) => i._idx).join(",")}`;

/** Produtos marcados como prontos (seleção, cartão do setor ou envio da mesa).
 *  Tudo pronto, a comanda sobe pra Prontos — o servidor faz o mesmo ao gravar. */
export function acaoMarcarProntos(pedido, itens) {
  const idxs = itens.map((i) => i._idx);
  const quando = new Date().toISOString();
  return {
    titulo: itens.length === 1 ? `Marcou pronto: ${nomeDe(itens[0])}` : `Marcou ${itens.length} produtos prontos`,
    alvo: alvoDe(pedido),
    chave: chave("pronto", pedido, itens),
    aplicar: (lista) => noPedido(lista, pedido.id, (p) => {
      const novo = nosItens(p, idxs, (i) => ({ ...i, pronto_at: i.pronto_at || quando }));
      const vivos = novo.items.filter((i) => !i.deleted);
      const tudoPronto = p.status === "producao" && vivos.length > 0 && vivos.every((i) => i.pronto_at);
      return tudoPronto ? { ...novo, status: "pronto" } : novo;
    }),
    requisicoes: itens.map((i) => patchItem(pedido, i, { pronto: true })),
    erro: "Parte dos produtos não foi marcada — confira a comanda",
  };
}

/** O "Desfazer" do produto que já estava pronto: ele volta pra produção. */
export function acaoDesmarcarPronto(pedido, item) {
  return {
    titulo: `Voltou para produção: ${nomeDe(item)}`,
    alvo: alvoDe(pedido),
    inversaDe: chave("pronto", pedido, [item]),
    aplicar: (lista) => noPedido(lista, pedido.id, (p) => nosItens(p, [item._idx], (i) => ({ ...i, pronto_at: null }))),
    requisicoes: [patchItem(pedido, item, { pronto: false })],
    erro: "Não deu pra desfazer",
  };
}

/** A comanda inteira muda de etapa: "Marcar Pronto" e "Concluir". Concluída,
 *  ela sai do KDS — aqui some da lista, em vez de ficar com um status que a
 *  tela não desenha. */
export function acaoStatus(pedido, status) {
  const concluir = status === "concluido";
  return {
    titulo: concluir ? "Concluiu o pedido" : "Marcou como pronto",
    alvo: alvoDe(pedido),
    aplicar: (lista) => (concluir ? lista.filter((p) => p.id !== pedido.id) : noPedido(lista, pedido.id, (p) => ({ ...p, status }))),
    // Não repetível: concluir duas vezes lançaria a venda em dobro no caixa.
    requisicoes: [{ metodo: "patch", url: `${API}/pedidos/${pedido.id}/status?status=${status}`, corpo: {}, repetivel: false }],
    erro: "Erro ao atualizar status",
    sucesso: concluir ? "Pedido concluído!" : "Status atualizado!",
  };
}

/** Prato entregue na mesa (ou a entrega desfeita). `daCozinha`: o botão
 *  "Entregue na mesa" do cartão de Prontos, que leva tudo de uma vez. */
export function acaoEntregar(pedido, itens, entregue = true, { daCozinha = false } = {}) {
  const idxs = itens.map((i) => i._idx);
  const quando = new Date().toISOString();
  const um = itens.length === 1 ? nomeDe(itens[0]) : null;
  const titulo = !entregue ? `Desfez a entrega: ${um || `${itens.length} itens`}`
    : daCozinha ? "Marcou como entregue na mesa"
      : um ? `Entregou: ${um}` : `Entregou ${itens.length} itens`;
  return {
    titulo,
    alvo: alvoDe(pedido),
    chave: entregue ? chave("entregue", pedido, itens) : undefined,
    inversaDe: entregue ? undefined : chave("entregue", pedido, itens),
    aplicar: (lista) => noPedido(lista, pedido.id, (p) => nosItens(p, idxs, (i) => ({
      ...i, entregue_at: entregue ? quando : null, pronto_at: i.pronto_at || (entregue ? quando : null),
    }))),
    requisicoes: itens.map((i) => patchItem(pedido, i, { entregue })),
    erro: entregue ? "Parte da entrega não foi marcada — confira a comanda" : "Não deu pra desfazer a entrega",
    sucesso: entregue && itens.length > 1 ? `${itens.length} itens entregues` : undefined,
  };
}

/** "Confirmar o cancelamento" do cartão que só tem produto cancelado na mesa:
 *  grava que a cozinha viu (vale pra todas as telas) e o cartão sai. */
export function acaoConfirmarCancelamento(card) {
  const itens = (card._itens || []).filter((i) => i._cancelado);
  const idxs = itens.map((i) => i._idx);
  const quando = new Date().toISOString();
  return {
    titulo: "Confirmou o cancelamento",
    alvo: alvoDe(card),
    aplicar: (lista) => noPedido(lista, card.id, (p) => nosItens(p, idxs, (i) => ({ ...i, cancelamento_visto_at: quando }))),
    requisicoes: itens.map((i) => patchItem(card, i, { cancelamento_visto: true })),
    erro: "Não consegui confirmar o cancelamento — tente de novo",
  };
}

/** Chamar o entregador da UP Entrega. Enquanto espera, o cartão mostra
 *  "Chamando entregador…" (`_upPendente`, lido em `acaoDoCard`) — a corrida só
 *  é pedida quando o tempo do Desfazer acaba. Pedir duas vezes abriria duas
 *  corridas: `repetivel` fica de fora. */
export function acaoChamarEntregador(pedido, { aoGravar, aoFalhar, depois } = {}) {
  return {
    titulo: "Chamou o entregador",
    alvo: alvoDe(pedido),
    aplicar: (lista) => noPedido(lista, pedido.id, (p) => ({ ...p, _upPendente: true })),
    requisicoes: [{
      metodo: "post", url: `${API}/entregaai/orders`, repetivel: false,
      // Espelho do handleChamarEntregador do Delivery: só forma_pagamento e
      // payment_status vão no payload — o backend resolve o resto pela config.
      corpo: {
        external_id: String(pedido.id),
        client: { name: pedido.cliente_nome || "Cliente", phone: pedido.cliente_telefone || "" },
        address: {
          street: pedido.endereco_rua || "", number: pedido.endereco_numero || "",
          complement: pedido.endereco_complemento || "", neighborhood: pedido.endereco_bairro || "",
          city: pedido.endereco_cidade || "", state: pedido.endereco_estado || "",
          reference: pedido.endereco_referencia || "",
        },
        order: { code: pedido.codigo || String(pedido.id).slice(0, 8), value: pedido.total || 0 },
        forma_pagamento: pedido.forma_pagamento || "",
        payment_status: pedido.payment_status || "",
      },
    }],
    aoGravar, aoFalhar, depois,
  };
}
